import React, { useState } from "react";
import { Table, Button, Upload, message, Space, Spin, Modal, Descriptions, Typography } from "antd";
import { UploadOutlined, FileExcelOutlined } from "@ant-design/icons";
import Layouts from "../../components/Layouts";
import { getserialesByserial } from "../../services/seriales";
import * as XLSX from "xlsx";

const PAGE_SIZE = 20; // Número de seriales por página

const SubirSeriales = () => {
  const [seriales, setSeriales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportTime, setExportTime] = useState(0);
  const [fileContent, setFileContent] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleUpload = async (file) => {
    setLoading(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        const lines = content.split(/\r?\n/).filter(line => line.trim() !== "");
        setFileContent(lines);
        setPagination(prev => ({
          ...prev,
          total: lines.length,
        }));
        message.success(`${lines.length} seriales cargados`);
        await fetchPageData(1, lines);
      } catch (error) {
        console.error("Error:", error);
        message.error("Error al procesar");
      } finally {
        setLoading(false);
      }
    };

    reader.readAsText(file);
    return false;
  };

  const fetchPageData = async (page, lines = fileContent) => {
    setLoading(true);
    try {
      const start = (page - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      const pageLines = lines.slice(start, end);

      const pageData = await Promise.all(
        pageLines.map(async (line) => {
          try {
            const apiData = await getserialesByserial(line.trim(), 1, PAGE_SIZE);
            const found = apiData[0];
            return {
              key: `${line}-${Date.now()}`,
              serial: line.trim(),
              estado: found?.estado?.estado || "No encontrado",
              bodega: found?.bodega?.nombre || "Sin asignar",
              producto: found?.producto,
              grupo: found?.producto?.grupo,
            };
          } catch (error) {
            console.error(`Error buscando serial ${line}:`, error);
            return {
              key: `${line}-${Date.now()}`,
              serial: line.trim(),
              estado: "Error en búsqueda",
              bodega: "Error en búsqueda",
              producto: null,
              grupo: null,
            };
          }
        })
      );

      setSeriales(pageData);
      setPagination(prev => ({
        ...prev,
        current: page,
      }));
    } catch (error) {
      console.error("Error:", error);
      message.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    fetchPageData(pagination.current);
  };

  const exportToExcel = async () => {
    try {
      setExporting(true);
      const estimatedTime = Math.ceil(fileContent.length * 50 / 1000);
      setExportTime(estimatedTime);
      
      // Procesar todos los seriales del archivo
      const allData = await Promise.all(
        fileContent.map(async (line) => {
          try {
            const apiData = await getserialesByserial(line.trim(), 1, PAGE_SIZE);
            const found = apiData[0];
            return {
              Serial: line.trim(),
              Estado: found?.estado?.estado || "No encontrado",
              Bodega: found?.bodega?.nombre || "Sin asignar",
              'Nombre Producto': found?.producto?.nombre || "N/A",
              Referencia: found?.producto?.referencia || "N/A",
              'Precio sin impuestos': found?.producto?.preciosinimp || "N/A",
              Impuestos: found?.producto?.grupo?.impuesto || "N/A",
              'Fecha creación': found?.producto?.date_created || "N/A"
            };
          } catch (error) {
            console.error(`Error buscando serial ${line}:`, error);
            return {
              Serial: line.trim(),
              Estado: "Error en búsqueda",
              Bodega: "Error en búsqueda",
              'Nombre Producto': "N/A",
              Referencia: "N/A",
              'Precio sin impuestos': "N/A",
              Impuestos: "N/A",
              'Fecha creación': "N/A"
            };
          }
        })
      );

      const ws = XLSX.utils.json_to_sheet(allData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Datos Completos");
      XLSX.writeFile(wb, "datos_completos.xlsx");
      message.success(`Exportación completada en ~${exportTime} segundos`);
    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      message.error("Error al exportar");
    } finally {
      setExporting(false);
      setExportTime(0);
    }
  };

  const showProductDetails = (record) => {
    setSelectedProduct({
      ...record.producto,
      grupo: record.grupo,
      serial: record.serial,
      estado: record.estado,
      bodega: record.bodega,
    });
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: "Serial",
      dataIndex: "serial",
      key: "serial",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (text) => (
        <span style={{ 
          color: text === "No encontrado" ? "red" : "inherit",
          fontWeight: text === "Buscando..." ? "bold" : "normal"
        }}>
          {text}
        </span>
      )
    },
    {
      title: "Bodega",
      dataIndex: "bodega",
      key: "bodega",
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Button 
          type="link" 
          onClick={() => showProductDetails(record)}
          disabled={!record.producto}
        >
          Detalles
        </Button>
      ),
    },
  ];

  return (
    <Layouts>
      <div style={{ padding: "20px" }}>
        <h2>Cargar Seriales</h2>
        <Space style={{ marginBottom: 16 }}>
          <Upload
            accept=".txt"
            beforeUpload={handleUpload}
            showUploadList={false}
            disabled={loading}
          >
            <Button icon={<UploadOutlined />} loading={loading}>
              Subir archivo (.txt)
            </Button>
          </Upload>
          <Button
            icon={<FileExcelOutlined />}
            onClick={exportToExcel}
            disabled={fileContent.length === 0 || exporting || loading}
          >
            {exporting ? `Exportando (~${exportTime}s)` : "Exportar a Excel"}
          </Button>
          <Typography.Text strong style={{ marginLeft: 8 }}>
              Mostrando: {seriales.length} de {fileContent.length} registros
          </Typography.Text>
        </Space>

        <Table
          columns={columns}
          dataSource={seriales}
          rowKey="key"
          loading={loading}
          style={{ marginTop: 20 }}
          pagination={{
            ...pagination,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
        />

        <Modal
          title="Detalles del Producto"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={800}
        >
          {selectedProduct && (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Serial">{selectedProduct.serial}</Descriptions.Item>
              <Descriptions.Item label="Nombre">{selectedProduct.nombre}</Descriptions.Item>
              <Descriptions.Item label="Referencia">{selectedProduct.referencia}</Descriptions.Item>
              <Descriptions.Item label="Estado">{selectedProduct.estado}</Descriptions.Item>
              <Descriptions.Item label="Bodega">{selectedProduct.bodega || "Sin asignar"}</Descriptions.Item>
              <Descriptions.Item label="Precio sin impuestos">{selectedProduct.preciosinimp}</Descriptions.Item>
              <Descriptions.Item label="Grupo">
                {selectedProduct.grupo ? (
                  <div>
                   
                    <p><strong>Impuestos:</strong> {selectedProduct.grupo.impuesto|| "N/A"}</p>
                  </div>
                ) : "N/A"}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </div>
    </Layouts>
  );
};

export default SubirSeriales;
